import { Component, ChangeDetectionStrategy, inject, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { AuthService, authErrorKey, authLinkErrorFromUrl } from '../../services/auth.service';
import { Router, RouterModule } from '@angular/router';
import { BrandLogoComponent } from '../../components/brand-logo/brand-logo.component';
import { BrandedLoaderComponent } from '../../components/branded-loader/branded-loader.component';
import { AuthAlertComponent } from '../../components/auth-alert/auth-alert.component';
import { fieldError } from '../../utils/password';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe, RouterModule, BrandLogoComponent, BrandedLoaderComponent, AuthAlertComponent],
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private router = inject(Router);

  loginForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', Validators.required],
  });

  // A failed confirmation link (expired, already used) lands here with the error in the URL.
  errorMessage = signal<string | null>(this.linkErrorMessage());
  isLoading = signal(false);
  /** Email of an account whose address is not confirmed yet, offered a new confirmation email. */
  unconfirmedEmail = signal<string | null>(null);
  isResending = signal(false);
  resentTo = signal<string | null>(null);

  readonly fieldError = fieldError;

  constructor() {
    // Already logged in, or just logged in by the confirmation link: go straight to the dashboard.
    effect(() => {
      // A reset link is sent to the new-password form by AuthService instead.
      if (this.authService.isInitialized() && this.authService.isAuthenticated() && !this.authService.passwordRecovery()) {
        void this.router.navigate(['/dashboard']);
      }
    });
  }

  async onSubmit() {
    if (this.isLoading()) return;
    this.loginForm.markAllAsTouched();
    this.errorMessage.set(null);
    this.unconfirmedEmail.set(null);
    this.resentTo.set(null);
    const { email, password } = this.loginForm.getRawValue();
    if (this.loginForm.invalid || !email || !password) return;

    this.isLoading.set(true);
    try {
      const { data, error } = await this.authService.signInWithPassword(email.trim(), password);
      if (error) {
        this.errorMessage.set(authErrorKey(error));
        if (error.code === 'email_not_confirmed') this.unconfirmedEmail.set(email.trim());
      } else if (data.session) {
        await this.router.navigate(['/dashboard']);
      }
    } catch (err) {
      console.error('Unexpected login error:', err);
      this.errorMessage.set('auth.error.unexpected');
    } finally {
      this.isLoading.set(false);
    }
  }

  async resendConfirmation() {
    const email = this.unconfirmedEmail();
    if (!email || this.isResending()) return;
    this.isResending.set(true);
    try {
      const { error } = await this.authService.resendConfirmation(email);
      if (error) {
        this.errorMessage.set(authErrorKey(error));
      } else {
        this.errorMessage.set(null);
        this.unconfirmedEmail.set(null);
        this.resentTo.set(email);
      }
    } finally {
      this.isResending.set(false);
    }
  }

  onCancel() {
    void this.router.navigate(['/races']);
  }

  private linkErrorMessage(): string | null {
    const code = authLinkErrorFromUrl();
    return code ? authErrorKey(code) : null;
  }
}
